import type { AngleTable } from './gait'
import { DOF_AXES } from './model'
import type { OffSet, OffState } from './state'

/**
 * Ausgeschaltete Achsen — deaktiviert, nicht gelöscht.
 *
 * WOZU. Wer wissen will, was eine Achse beiträgt, nimmt sie heraus und schaut.
 * Sie dafür zu löschen hiesse, sie danach mit allen Zahlen wieder aufzubauen —
 * und bis dahin gäbe es keinen Weg zurück. Eine ausgeschaltete Achse behält
 * ihre Grenzen, ihr Moment und ihren Platz im Baum; sie steht nur still.
 *
 * WAS DAS AUSSCHALTEN WIRKLICH TUT, in dieser Reihenfolge:
 *
 *   1. Der Winkel dieser Ebene wird im Gang auf den Rastwinkel gesetzt. Nicht
 *      gedämpft, nicht geglättet — festgehalten.
 *   2. Der Bodenkontakt rechnet danach neu. Ein festes Knie bringt den Fuss
 *      woanders hin, und die Meldung über Durchdringung oder Schweben ist die
 *      ehrliche Auskunft darüber.
 *   3. Die Achse verschwindet aus den Grenzen — sie hat keinen Bereich mehr.
 *   4. Im Antrieb entscheidet `drive`, siehe unten.
 *
 * WAS ES NICHT TUT: die Bahn neu planen. Der Gang wurde für die freie Maschine
 * gerechnet; das Einfrieren kommt danach. Das ist Absicht — die Frage lautet
 * „was passiert, wenn diese Achse ausfällt", nicht „wie liefe eine Maschine,
 * die von vornherein ohne sie entworfen wurde". Die zweite Frage beantwortet
 * man, indem man den Gang danach neu einstellt.
 */

/** Wie viele Freiheiten stillstehen — und bei wie vielen der Antrieb bleibt. */
export function offCount(off: OffSet): { stopped: number; kept: number } {
  const keys = Object.keys(off)
  return { stopped: keys.length, kept: keys.filter((k) => off[k].drive).length }
}

/**
 * Steht diese Ebene dieses Gelenks still, und ist ihr Antrieb ausgebaut?
 *
 * Die Antriebsliste kennt Gelenk und Ebene, nicht die Kennung der
 * Winkelfreiheit. Diese Übersetzung geht über `DOF_AXES` — dieselbe Tabelle,
 * aus der auch die Grenzen kommen.
 */
export function planeOff(off: OffSet, joint: string, plane: string): OffState | undefined {
  for (const spec of DOF_AXES) {
    if (spec.joint !== joint || spec.plane !== plane) continue
    const state = off[spec.dof]
    if (state) return state
  }
  return undefined
}

/**
 * Setzt die Winkel der stillgelegten Ebenen auf ihren Rastwinkel.
 *
 * VORZEICHEN. Der Rastwinkel steht in der Zählweise der Winkelfreiheit —
 * positiv nach aussen. Die aufgezeichneten Kurven zählen für die Frontalachsen
 * positiv nach innen. Gespiegelt wird deshalb hier, mit derselben Angabe, aus
 * der auch `planeLimits` seine gespiegelten Grenzen bildet. Zwei Tabellen für
 * dieselbe Umrechnung wären zwei Gelegenheiten, sie unterschiedlich zu machen.
 *
 * Gibt den EINGABEWERT zurück, wenn nichts ausgeschaltet ist — der Normalfall
 * kostet damit keine Kopie.
 */
export function freezeAngles(angles: AngleTable, off: OffSet): AngleTable {
  const specs = DOF_AXES.filter((spec) => off[spec.dof])
  if (specs.length === 0) return angles

  const out: AngleTable = {}
  for (const joint of Object.keys(angles)) out[joint] = { ...angles[joint] }

  for (const spec of specs) {
    const planes = out[spec.joint]
    if (!planes) continue
    const table = planes[spec.plane]
    if (!table) continue

    const at = off[spec.dof]!.at
    const value = spec.mirror ? -at : at

    /* Beide Seiten. Eine Achse steht links wie rechts still — sie ist EINE
       Konstruktionsentscheidung, keine zwei. */
    planes[spec.plane] = {
      R: fill(table.R.length, value),
      L: fill(table.L.length, value),
    }
  }

  return out
}

const fill = (n: number, value: number): Float32Array => {
  const out = new Float32Array(n)
  out.fill(value)
  return out
}
